import moment, { Moment } from "moment";
import Result from "./result.utils";
import { DealBuilderInputStore } from "../../stores/deal-builder-inputs.store.ts";

const LOWEST_POSSIBLE_MARKET_CONDITIONS_DATE = moment("2021-06-01", "YYYY-MM-DD")

export function validateDealBuilderInputs(inputs: DealBuilderInputStore): Result<undefined> {

    // The total principal amount has to be divisible by the denomination
    // amount.
    if (inputs.parAmount % inputs.denomination !== 0) {
        return { valid: false, error: "The Par Amount must be divisible by  Denomination" };
    }

    // The denominations must be equal or smaller than the total principal, if not, it's
    // impossible to reach the total principal without going above it.
    if (inputs.parAmount < inputs.denomination) {
        return { valid: false, error: "The Par Amount must be bigger than the Denomination" };
    }
    
    // The date of market conditions can't be too old, otherwise we don't have
    // data calculated on the backend
    if (inputs.marketConditionsDate.isBefore(LOWEST_POSSIBLE_MARKET_CONDITIONS_DATE)) {
        const error = `The Date of Market Conditions must be after ${LOWEST_POSSIBLE_MARKET_CONDITIONS_DATE.format("MMMM D, YYYY")}`
        return { valid: false, error: error };
    }

    // The date of market conditions must be in the past.
    if (inputs.marketConditionsDate.startOf("day").isAfter(moment().startOf("day"))) {
        return { valid: false, error: "The Date of Market Conditions can't be in the future." };
    }

    // The first coupon date must be after the delivery date; it doesn't make
    // sense for someone to get coupon payments before buying a bond.
    if (inputs.firstCouponDate.isBefore(inputs.deliveryDate)) {
        return { valid: false, error: "The First Coupon Date must be after the Delivery Date." };
    }

    // Our predictions are based on the market conditions date; it doesn't make
    // sense to predict stuff that happened in the past.
    if (inputs.firstCouponDate.startOf("day").isBefore(inputs.marketConditionsDate.startOf("day"))) {
        return { valid: false, error: "The First Coupon Date must be after the Market Conditions Date." };
    }
    
    // It doesn't make sense for the first coupon to be after the first principal
    // date, that would mean the bond didn't have a single coupon payment.
    if (inputs.firstPrincipalDate.startOf("day").isBefore(inputs.firstCouponDate.startOf("day"))) {
        return { valid: false, error: "The First Principal Date must be the same or after the First Coupon Date." };
    }

    // Our predictions are based on the market conditions date; it doesn't make
    // sense to predict stuff that happened in the past.
    if (inputs.firstPrincipalDate.startOf("day").isBefore(inputs.marketConditionsDate.startOf("day"))) {
        return { valid: false, error: "The First Principal Date must be after the Market Conditions Date." };
    }

    // The date of the first principal must match the first coupon by multiples 
    // of 6 months because our coupon payments are semi annuals.
    const validFirstPrincipalDate = validatePrincipalDate(
        inputs.firstCouponDate, 
        inputs.firstPrincipalDate,
        2
    );

    if (!validFirstPrincipalDate.valid) {
        return { valid: false, error: "The First Principal Date must end on " + validFirstPrincipalDate.error };
    }

    // The final principal date must be after the first principal date
    if (inputs.finalPrincipalDate.isBefore(inputs.firstPrincipalDate)) {
        return { valid: false, error: "The Final Principal Date must be after the First Principal Date." };
    }

    // The last principal date must be at must 30 years from the date of market
    // conditions because the backend calculates a maximum of 30 years.
    const maxPossibleDate = inputs.marketConditionsDate.clone().add(30, "years");
    if (inputs.finalPrincipalDate.isAfter(maxPossibleDate)) {
        return { valid: false, error: "The Final Principal Date can't be more than 30 years from the Market Conditions Date." };
    }

    // The date of the final principal must match the first principal.
    const validFinalPrincipalDate = validatePrincipalDate(
        inputs.firstPrincipalDate, 
        inputs.finalPrincipalDate,
        inputs.principalRate
    );

    if (!validFinalPrincipalDate.valid) {
        return { valid: false, error: "The Final Principal Date must end on " + validFinalPrincipalDate.error };
    }

    // The Call Date must before last principal dates.
    if (inputs.useCallDate && inputs.callDate.isAfter(inputs.finalPrincipalDate, "day")) {
        return { valid: false, error: "The Call Date must be before the Final Principal Dates." };
    }

    // All term bonds start date must be before than the end date
    for (const [start, end] of inputs.termBonds) {
        if (end.isSameOrBefore(start)) {
            return { valid: false, error: "The start date of the term bonds must be before the end date."};
        }
    }

    // All term bond dates must be between the first and last principal
    for (const [start, end] of inputs.termBonds) {
        if (start.isBefore(inputs.firstPrincipalDate) || end.isAfter(inputs.finalPrincipalDate)) {
            return { valid: false, error: "The term bond dates must be between the first and final principal dates."};
        }
    }

    // Prevent term bond overlap
    const sortedTermBonds = [ ...inputs.termBonds ].sort((a, b) => a[0].isBefore(b[0]) ? -1 : 1);
    for (let index = 1; index < sortedTermBonds.length; index++) {
        if (sortedTermBonds[index][0].isSameOrBefore(sortedTermBonds[index - 1][1])) {
            return { valid: false, error: "The term bond dates can't overlap."};
        }
    }

    // Term bonds must match the day/month of the first principal date, or a 6
    // months offset if the frequency is 6 months.
    const validTermBondDates: string[] = [inputs.firstPrincipalDate.format("MM/DD")];
    if (inputs.principalRate === 2) {
        validTermBondDates.push(inputs.firstPrincipalDate.clone().add(6, "months").format("MM/DD"))
    }

    for (const [start, end] of inputs.termBonds) {
        if (
            !validTermBondDates.includes(start.format("MM/DD")) || 
            !validTermBondDates.includes(end.format("MM/DD"))
        ) {
            return { valid: false, error: "The term bond dates must be aligned with your principals."};
        }
    }

    return { valid: true };
}

/**
 * Checks if a principal date is valid, if not, it returns a list
 * of valid dates in a string format.
 */
function validatePrincipalDate(
    startDate: Moment, 
    endDate: Moment, 
    frequency: number
): Result<undefined> {
    const monthDayFormat = "MM/DD";
    const [start, end] = [moment(startDate), moment(endDate)];
    const endMonthDay = end.format(monthDayFormat);

    // If it's annual frequency, the dates must be multiples of 1 year.
    if (frequency === 1) {
        if (endMonthDay !== start.format(monthDayFormat)) {
            return { valid: false, error: start.format("MMM Do") };
        }
        return { valid: true };
    }

    // Semi annual, there's 2 valid dates
    const startMonth = start.month();
    const validAltMonth = startMonth > 6 ? startMonth - 6 : startMonth + 6;
    const validAltDate = moment(start).month(validAltMonth);

    if (
        endMonthDay === start.format(monthDayFormat) ||
        endMonthDay === validAltDate.format(monthDayFormat)
    ) {
        return { valid: true };
    } else {
        return { valid: false, error: start.format("MMM Do") + " or " + validAltDate.format("MMM Do") };
    }
    
}

/**
 * Translates range of term bonds into an array of booleans aligned
 * with the principal's dates.
 */
export function termBondDatesToBooleans(termBonds: [Moment, Moment][], principals: string[]): boolean[] {
    if (principals.length === 1) return [false];

    const sortedTermDates = [ ...termBonds ].sort((a, b) => a[0].isBefore(b[0]) ? -1 : 1);
    const result: boolean[] = [];

    let currTermBondIndex = 0;
    for (let i = 0; i < principals.length; i++) {
        const principalDate = moment(principals[i]);

        if (currTermBondIndex === termBonds.length) {
            result.push(false);
            continue;
        }

        const [termStart, termEnd] = sortedTermDates[currTermBondIndex];
        // Option 1: Not reached yet
        if (principalDate.isSameOrBefore(termStart, "day")) {
            result.push(false);
        }

        // Option 2: During the current period
        else if (principalDate.isSameOrBefore(termEnd, "day")) {
            result.push(true);
        }

        // Option 3: Last period of the current term bond
        else {
            result.push(false);
            currTermBondIndex += 1;
        }
    }

    return result;
}
